/**
 * AI Workflow Builder Component
 * Natural language workflow generation
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Sparkles, Wand2, AlertCircle, CheckCircle, Clock, Users, ArrowRight } from 'lucide-react';
import agenticApi from '../../api/agenticApi';
import { toast } from 'sonner';

const WorkflowBuilder = ({ headers, language = 'en', onWorkflowCreated }) => {
  const [description, setDescription] = useState('');
  const [documentTypes, setDocumentTypes] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const generateWorkflow = async () => {
    if (!description.trim()) {
      toast.error('Please describe your workflow');
      return;
    }

    setLoading(true);
    try {
      const docTypes = documentTypes.split(',').map(t => t.trim()).filter(Boolean);
      const response = await agenticApi.generateWorkflow(description, docTypes, language, headers);
      setSuggestion(response.data);
      toast.success('Workflow suggestion generated!');
    } catch (err) {
      console.error('Workflow generation failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate workflow');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!suggestion) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('reasondms_token');
      const preview = suggestion.suggestion || suggestion.preview || suggestion || {};
      
      // Create workflow in the database via the workflows API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workflows/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: preview.name || suggestion.suggestion?.name || 'AI Generated Workflow',
          json_definition: preview.json_definition || {
            steps: (suggestion.suggestion?.steps || []).map(s => ({
              name: s.name,
              allowed_roles: s.allowed_roles || ["TenantAdmin"],
              sla_hours: s.sla_hours || 24,
              approvers: s.allowed_roles || ["TenantAdmin"]
            }))
          },
          is_active: true
        })
      });
      
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to create workflow');
      }
      
      const created = await response.json();
      toast.success('Workflow created successfully!');
      
      if (onWorkflowCreated) {
        onWorkflowCreated(created);
      }
    } catch (err) {
      console.error('Workflow creation failed:', err);
      toast.error(err.message || 'Failed to create workflow');
    } finally {
      setLoading(false);
      setDialogOpen(false);
      setSuggestion(null);
      setDescription('');
      setDocumentTypes('');
    }
  };

  const getSlaLabel = (hours) => {
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          className="border-purple-300 hover:bg-purple-50 text-purple-700"
          data-testid="ai-workflow-builder-btn"
        >
          <Wand2 className="w-4 h-4 mr-2" />
          AI Workflow Builder
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            AI Workflow Builder
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Input Section */}
          <Card>
            <CardContent className="pt-4 space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-2">
                  Describe your workflow in natural language
                </label>
                <Input
                  placeholder="e.g., Finance approval then legal review for contracts"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full"
                  data-testid="workflow-description-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-2">
                  Document types (optional, comma-separated)
                </label>
                <Input
                  placeholder="e.g., Contract, Invoice"
                  value={documentTypes}
                  onChange={(e) => setDocumentTypes(e.target.value)}
                  className="w-full"
                />
              </div>

              <Button 
                onClick={generateWorkflow}
                disabled={loading || !description.trim()}
                className="w-full bg-purple-600 hover:bg-purple-700"
                data-testid="generate-workflow-btn"
              >
                {loading ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-2 animate-pulse" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generate Workflow
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Suggestion Preview */}
          {suggestion && (
            <Card className="border-purple-200 bg-purple-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Generated Workflow
                  <Badge variant="outline" className="ml-auto text-xs">
                    Preview
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Workflow Name & Description */}
                <div>
                  <h4 className="font-semibold text-slate-800">{suggestion.suggestion?.name}</h4>
                  <p className="text-sm text-slate-600">{suggestion.suggestion?.description}</p>
                </div>

                {/* Steps Visualization */}
                <div className="space-y-2">
                  <h5 className="text-sm font-medium text-slate-700">Workflow Steps</h5>
                  <div className="flex flex-wrap items-center gap-2">
                    {suggestion.suggestion?.steps?.map((step, index) => (
                      <React.Fragment key={index}>
                        <div className="flex items-center gap-2 p-2 bg-white rounded-lg border shadow-sm">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-slate-800">{step.name}</div>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="secondary" className="text-xs">
                                <Users className="w-3 h-3 mr-1" />
                                {step.allowed_roles?.join(', ')}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                {getSlaLabel(step.sla_hours)}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        {index < suggestion.suggestion?.steps?.length - 1 && (
                          <ArrowRight className="w-4 h-4 text-slate-400" />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* Warning */}
                <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <div className="text-xs text-amber-800">
                    <strong>Admin Confirmation Required:</strong> This is a suggestion only. 
                    Review the workflow configuration and click "Confirm & Create" to proceed.
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setSuggestion(null)}
                  >
                    Modify
                  </Button>
                  <Button 
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={handleConfirm}
                    data-testid="confirm-workflow-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Confirm & Create
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowBuilder;
